import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@zg-rentals/react-query5';

import { IconPanorama360Outline, IconPlusCircleFilled } from '@zillow/constellation-icons';
import { Box, Button, Icon, Paragraph, Text, TextButton } from '@zillow/constellation-10';

import {
  useRentalManagerUserPropertyVrModelWebApiHandler_associateListing,
  useRentalManagerUserPropertyVrModelWebApiHandler_associateModel,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import { useSelector } from '../../../../selectors/useSelector';

import PropertiesActions from '../../../../actions/PropertiesActions';

import PostingActions from '../../../../actions/PostingActions';

import { getDefaultTourLabel, getModelId, updateTour } from '../../../../../client/utils/3dToursUtils';

import ErrorMessageConstants from '../../../../constants/ErrorMessageConstants';
import { GET_LISTING_RECS, RECS } from '../../../../constants/RecommendationsConstants';

import ExternalTour from '../../../../model/ExternalTour';

import SelectTourModal from '../../../../components/Modal/SelectTourModal';
import RecommendationsLearnMoreContent from '../../../../components/RecommendationsLearnMoreContent';

import { emptyTourCardContainerStyles } from '../styles';

import type { VrModelProps } from '../../../../model/VrModel';
import type { ExternalTourProps } from '../../../../model/ExternalTour';
import type { KeysOfTourType } from '../../../../../client/utils/3dToursUtils';

type Props = {
  isDisabled?: boolean;
  isHidden?: boolean;
  listingToursCount: number;
  tourType: KeysOfTourType;
  floorPlanIndex?: number;
  unitIndex?: number;
  isRelistingFieldset?: boolean;
};

type handleAssociateArgs = {
  shouldAssociate?: boolean;
  tourId?: string;
  externalTour?: ExternalTourProps;
};

const TourCardEmpty = ({
  floorPlanIndex,
  unitIndex,
  tourType,
  listingToursCount,
  isDisabled,
  isHidden,
  isRelistingFieldset = false,
}: Props) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { mutateAsync: associateVrModel } = useRentalManagerUserPropertyVrModelWebApiHandler_associateModel();
  const { mutateAsync: associateVrModelListing } = useRentalManagerUserPropertyVrModelWebApiHandler_associateListing();

  const [displaySelectTourModal, setDisplaySelectTourModal] = useState(false);
  const [displayRecsModal, setDisplayRecsModal] = useState(false);

  const {
    user: { isMultifamilyCustomer, isSingleFamilyEnterpriseCustomer },
    properties: {
      currentProperty,
      currentProperty: { syndication, isMultifamily, listingAlias },
    },
  } = useSelector((state) => state);

  // @ts-expect-error
  const updateCurrentProperty = (data) => dispatch(PropertiesActions.updateCurrentProperty(data));

  const handleAddOrEditTourClick = () => {
    setDisplaySelectTourModal(true);
  };

  const handleAssociate = ({ shouldAssociate = false, tourId, externalTour }: handleAssociateArgs) => {
    const isFloorPlanorUnit3dTour =
      (floorPlanIndex !== undefined && floorPlanIndex > -1) || (unitIndex !== undefined && unitIndex > -1);

    const apiToCall = isFloorPlanorUnit3dTour ? 'associateModel' : 'associateListing';
    const modelId =
      isFloorPlanorUnit3dTour &&
      getModelId({
        floorPlanIndex,
        currentProperty,
        unitIndex,
      });

    if (tourId) {
      if (isFloorPlanorUnit3dTour) {
        return associateVrModel({
          getParams: {
            vrModelId: Number(tourId),
            listingAlias,
            shouldAssociate: !!shouldAssociate,
            encodedFloorplanKey: (modelId as string) ?? '',
          },
        });
      }
      return associateVrModelListing({
        getParams: { vrModelId: Number(tourId), listingAlias, shouldAssociate: !!shouldAssociate },
      });
    }
    if (externalTour) {
      return dispatch(
        PostingActions.property.externalTour[apiToCall](externalTour, listingAlias, shouldAssociate, modelId),
      );
    }
    return Promise.resolve();
  };

  const handleAdd = (isZillowTour: boolean, newSelectedTourId: string, newExternalTour: ExternalTourProps) => {
    return (
      handleAssociate({
        shouldAssociate: true,
        ...(isZillowTour && { tourId: newSelectedTourId }),
        ...(!isZillowTour && { externalTour: newExternalTour }),
      })
        // @ts-expect-error
        .then((tour: VrModelProps | ExternalTourProps) => {
          updateTour({
            floorPlanIndex: floorPlanIndex as number,
            unitIndex: unitIndex as number,
            currentProperty,
            tourToUpdate: tour,
            tourType,
            updateCurrentProperty,
            isExternalTour: !isZillowTour,
          });
          queryClient.invalidateQueries({ queryKey: [GET_LISTING_RECS] });
          setDisplaySelectTourModal(false);
        })
        .catch((errMessage: string) => {
          if (isZillowTour) {
            Promise.reject('Cannot associate this tour, please try again later.');
          } else {
            if (errMessage && errMessage.toLowerCase().indexOf('invalid url') > -1) {
              return Promise.reject(ErrorMessageConstants.EXTERNAL_TOUR_INVALID_URL);
            }
            if (errMessage && errMessage.toLowerCase().indexOf('tour already exists') > -1) {
              return Promise.reject(ErrorMessageConstants.EXTERNAL_TOUR_ALREADY_EXIST);
            }
            if (errMessage && errMessage.toLowerCase().indexOf('is not from a supported provider') > -1) {
              return Promise.reject(ErrorMessageConstants.EXTERNAL_TOUR_NOT_SUPPORTED_PROVIDER);
            }
            if (errMessage && errMessage.toLowerCase().indexOf('doesn’t have permission to add external tours') > -1) {
              return Promise.reject(
                ErrorMessageConstants.EXTERNAL_TOUR_NO_PERMISSION(
                  isMultifamily && isMultifamilyCustomer,
                  isSingleFamilyEnterpriseCustomer,
                ),
              );
            }
            return Promise.reject('Cannot associate this tour, please try again later.');
          }
        })
    );
  };

  const handleSelectTourSubmit = (
    isZillowTour: boolean,
    newSelectedTourId: string,
    newExternalTour: ExternalTourProps,
  ) => {
    return handleAdd(isZillowTour, newSelectedTourId, newExternalTour);
  };

  const renderContent = () => {
    if (isRelistingFieldset) {
      return (
        <>
          <Text textStyle={isRelistingFieldset ? 'body' : 'body-sm'}>
            Add a 3D tour using the Zillow 3D Home® app or an external link.
          </Text>
          <Box css={{ display: 'flex', flexDirection: 'row', paddingTop: '400' }}>
            <Button
              tone="neutral"
              emphasis="outlined"
              size="sm"
              disabled={isDisabled}
              onClick={handleAddOrEditTourClick}
            >
              Add 3D tour
            </Button>
            <TextButton
              textStyle={isRelistingFieldset ? 'body-bold' : 'body-sm-bold'}
              css={{ paddingLeft: '400' }}
              onClick={() => {
                setDisplayRecsModal(true);
              }}
            >
              Learn more
            </TextButton>
          </Box>
        </>
      );
    }
    return (
      <Box css={{ ...emptyTourCardContainerStyles, maxWidth: listingToursCount === 0 ? '100%' : '270px' }}>
        <Icon css={{ marginBottom: '400' }} size="lg" render={<IconPanorama360Outline />} />
        {isHidden ? (
          <Paragraph textStyle={listingToursCount === 0 ? 'body' : 'body-xs'}>
            Floor plans with units only show 3D tours at the unit level. Please add your 3D tour to individual units to
            have 3D tours show on our sites.
          </Paragraph>
        ) : (
          <>
            <Paragraph textStyle={listingToursCount === 0 ? 'body' : 'body-xs'} css={{ marginBottom: '400' }}>
              Add via Zillow 3D Home app or an external link
            </Paragraph>
            <Button
              size="sm"
              tone="brand"
              emphasis="outlined"
              disabled={isDisabled}
              onClick={handleAddOrEditTourClick}
              data-testid="TourCardEmpty-AddTour"
              icon={<IconPlusCircleFilled />}
            >
              Add tour
            </Button>
          </>
        )}
      </Box>
    );
  };

  return (
    <>
      {renderContent()}
      {displaySelectTourModal && (
        <SelectTourModal
          zillowDisplayStatus={syndication?.zillow?.displayStatus?.toLowerCase()}
          tourType={tourType}
          onSubmit={handleSelectTourSubmit}
          onClose={() => setDisplaySelectTourModal(false)}
          onCancel={() => setDisplaySelectTourModal(false)}
          filterOutVrModelIds={[]}
          initialExternalTour={{
            ...ExternalTour(),
            label: getDefaultTourLabel(tourType, floorPlanIndex, unitIndex, currentProperty),
          }}
        />
      )}
      {isRelistingFieldset && displayRecsModal && (
        <RecommendationsLearnMoreContent
          onClose={() => setDisplayRecsModal(false)}
          recommendationType={RECS?.VIRTUAL_TOUR}
          isFromRelistingFlow={true}
        />
      )}
    </>
  );
};

export default TourCardEmpty;
